import styled from '@emotion/styled'
import { CartIconStyle } from '@storefronts/common-types'
import { useContext } from 'react'
import { CartContext } from '../../context/cartContext'
import { PageContext } from '../../context/PageContextProvider'
import {
    BasketIcon,
    CartIcon as BagIcon,
    ShoppingCartIcon,
} from '../icons/Icons'
import {
    ACCENT_FILL_COLOR_VAR,
    ACCENT_TEXT_COLOR_VAR,
    CustomizationContext,
    PRIMARY_ICON_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { getTypographyRoleStyle } from '../../style/typography'
import { scaledFontSize } from '../../style/scrollingLayout'
import { hoverFadeText } from '../../style/interactiveStates'

const ICON_BY_STYLE: Record<
    CartIconStyle,
    React.FC<{ size?: number; color?: string }>
> = {
    BAG: BagIcon,
    CART: ShoppingCartIcon,
    BASKET: BasketIcon,
}

const CartQuantity = styled.div({
    position: 'absolute',
    top: -4,
    right: -4,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: `var(${ACCENT_FILL_COLOR_VAR})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...getTypographyRoleStyle('badgeText', {
        fontWeight: 600,
    }),
    fontSize: scaledFontSize(10),
    color: `var(${ACCENT_TEXT_COLOR_VAR})`,
})

const ICON_SIZE = 32
const CONTAINER_SIZE = ICON_SIZE + 6

const CartIconButton = styled.button({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: CONTAINER_SIZE,
    height: CONTAINER_SIZE,
    padding: 0,
    border: 'none',
    background: 'transparent',
    borderRadius: '50%',
    cursor: 'pointer',
    ...hoverFadeText(),
})

export const CartIcon: React.FC = () => {
    const { cart } = useContext(CartContext)
    const { setIsCartOpen } = useContext(PageContext)
    const { cartIconStyle = 'BAG' } = useContext(CustomizationContext)
    const Icon = ICON_BY_STYLE[cartIconStyle]

    const quantity = cart.items.reduce(
        (total, item) => total + item.quantity,
        0,
    )

    return (
        <CartIconButton
            type="button"
            aria-label={quantity > 0 ? `Open cart (${quantity})` : 'Open cart'}
            onClick={() => setIsCartOpen(true)}
        >
            <Icon size={ICON_SIZE} color={`var(${PRIMARY_ICON_COLOR_VAR})`} />
            {quantity > 0 && (
                <CartQuantity aria-hidden="true">
                    <span>{quantity}</span>
                </CartQuantity>
            )}
        </CartIconButton>
    )
}
